import classnames from 'classnames/bind'
import React, {
  ButtonHTMLAttributes,
  DOMAttributes,
  ReactNode,
  useRef,
} from 'react'
import { UrlObject } from 'url'

import { useIsHover, useTimeline } from '@unlikelystudio/react-hooks'

import Link, { LinkProps } from '~/components/Link'

import { useStyle } from '~/providers/StyleProvider'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type RoundedCtaTextPresets = 'cta-small' | 'cta-label' | 'inherit'

interface BaseProps extends DOMAttributes<any> {
  className?: string
  ctaAndChildrenClassName?: string
  type?: ButtonHTMLAttributes<any>['type']
  href?: string | UrlObject
  children?: ReactNode
  theme?: 'primary' | 'secondary' | 'tertiary'
  size?: 'normal' | 'small'
  isLoading?: boolean
  ctaType?: 'rounded'
  textPreset?: RoundedCtaTextPresets
}

export type RoundedCtaProps = BaseProps | (LinkProps & BaseProps)

/**
 * Rounded cta component
 * @param props
 * @returns Component
 */
function RoundedCta({
  className,
  ctaAndChildrenClassName,
  children,
  href,
  theme = 'primary',
  size = 'normal',
  type,
  onMouseEnter,
  onMouseLeave,
  isLoading,
  ctaType,
  textPreset = 'cta-label',
  ...rest
}: RoundedCtaProps) {
  const [isHover, mouseEvents] = useIsHover({
    onMouseEnter,
    onMouseLeave,
  })

  const ctaTextStyle = useStyle({
    textPreset,
  })

  const classNames = cx(
    className,
    css.RoundedCta,
    ctaTextStyle,
    ctaAndChildrenClassName,
    css?.[theme],
    css?.[size],
  )

  const squaredCtaChildren = (
    <div>
      <span>{children}</span>
    </div>
  )

  return href ? (
    <Link href={href} className={classNames} {...mouseEvents} {...rest}>
      {squaredCtaChildren}
    </Link>
  ) : (
    <button type={type} className={classNames} {...mouseEvents} {...rest}>
      {squaredCtaChildren}
    </button>
  )
}

export type { LinkProps } from '~/components/Link'

export default RoundedCta
